import moment from 'moment';
import SparkMD5 from 'spark-md5';

const rangeOfFullYearsWithFuture = (start, end) => {
  const fullYears = (end.getTime() - start.getTime()) / 31536000000;
  return Array(end.getFullYear() - start.getFullYear() + 1)
    .fill(start.getFullYear())
    .map((year, index) => year + index)
    .filter((year, index) => index <= fullYears)
    .map((year) => ({ label: year, value: year }));
};

const rangeOfFullYears = (startYear = 1920, type = 'forward') => {
  let currentYear = new Date().getFullYear();
  const yearRange = [];

  if (type === 'forward') {
    while (startYear <= currentYear) {
      const year = startYear++;
      yearRange.push({ label: year, value: year });
    }
  } else {
    while (currentYear >= startYear) {
      const year = currentYear--;
      yearRange.push({ label: year, value: year });
    }
  }

  return yearRange;
};

// const days = Array.from({length: 31}, (_, i) => i + 1).map(val => ({value:val, label: val}))

const getDaysInMonth = (month, year) => {
  // var date = new Date(year, month, 1);
  var date = new Date(Date.UTC(year, month, 1));
  var days = [];
  while (date.getUTCMonth() === month) {
    days.push(date.getUTCDate());
    date.setUTCDate(date.getUTCDate() + 1);
  }
  let finalDaysData = days.map((val) => ({ value: val, label: val }));

  return finalDaysData;
};

const meetingsByDate = (meetings) =>
  meetings?.reduce((acc, meeting) => {
    const date = new Date(meeting?.start_time);
    const key = isToday(date)
      ? 'Today'
      : isTomorrow(date)
      ? 'Tomorrow'
      : moment(date).format('ddd MMM D, YYYY');
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(meeting);
    return acc;
  }, {});

const todayDate = moment().format('YYYY-MM-DD');
const presentTime = moment();
const tomorrowDate = moment().startOf('day').add(1, 'day').format('YYYY-MM-DD');
const endOfToday = moment().endOf('day');

const isToday = (meetingDate) => {
  return moment(meetingDate).format('YYYY-MM-DD') === todayDate;
};

const isTomorrow = (meetingDate) => {
  return moment(meetingDate).format('YYYY-MM-DD') === tomorrowDate;
};

const isAfterTomorrow = (meetingDate) => {
  return moment(meetingDate).isAfter(tomorrowDate);
};

const isAfterPresentTimeAndToday = (meetingDate) => {
  return (
    moment(meetingDate).isAfter(presentTime) &&
    moment(meetingDate).isBefore(endOfToday)
  );
};

const isAfterPresentTime = (meetingDate) => {
  return moment(meetingDate).isAfter(presentTime);
};

const isBeforeToday = (meetingDate) => {
  return moment(meetingDate).isBefore(todayDate);
};

/**
 * Trims a long string and adds ellipsis(...) at the end
 * @param {*} str
 * @param {*} opts {length(number), appendSuffix(boolean), suffix(string)}
 * @returns
 */
export const trimStr = (str, opts) => {
  const { length = 20, appendSuffix = true, suffix = '...' } = opts || {};
  return str?.length > length
    ? str?.substring(0, length - 1) + (appendSuffix ? suffix : '')
    : str;
};

/**
 * Convers a string to pascal case
 * @param {*} str
 * @returns
 */
export const toPascalCase = (str) => {
  return str.replace(/(\w)(\w*)/g, function (g0, g1, g2) {
    return g1.toUpperCase() + g2.toLowerCase();
  });
};

const isImageURL = (url) => {
  return /(http(s?):)([/|.|\w|\s|-])*\.(?:jpg|gif|png|jpeg)/g.test(url);
};

/**
 * Generate random ID of specific length
 * @param {*} length
 * @returns
 */
export const randomId = (length = 5) => {
  var result = '';
  var characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export function fallbackCopyTextToClipboard(text) {
  var textArea = document.createElement('textarea');
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    var successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    document.body.removeChild(textArea);
    console.error('Fallback: Oops, unable to copy', err);
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

export async function copyTextToClipboard(text) {
  if (!navigator.clipboard) {
    return fallbackCopyTextToClipboard(text);
  }
  await navigator.clipboard.writeText(text);
  return true;
}

// Country code to currency code mapping based on your API response
const countryToCurrencyMap = {
  'AF': 'AFN', // Afghanistan -> Afghani
  'EU': 'EUR', // Euro
  'AL': 'ALL', // Albania -> Lek
  'DZ': 'DZD', // Algeria -> Algerian Dinar
  'US': 'USD', // US Dollar
  'AO': 'AOA', // Angola -> Kwanza
  'XC': 'XCD', // East Caribbean Dollar
  'AR': 'ARS', // Argentina -> Argentine Peso
  'AM': 'AMD', // Armenia -> Armenian Dram
  'AW': 'AWG', // Aruba -> Aruban Florin
  'AU': 'AUD', // Australia -> Australian Dollar
  'AZ': 'AZN', // Azerbaijan -> Azerbaijan Manat
  'BS': 'BSD', // Bahamas -> Bahamian Dollar
  'BH': 'BHD', // Bahrain -> Bahraini Dinar
  'BD': 'BDT', // Bangladesh -> Taka
  'BB': 'BBD', // Barbados -> Barbados Dollar
  'BY': 'BYN', // Belarus -> Belarusian Ruble
  'BZ': 'BZD', // Belize -> Belize Dollar
  'XO': 'XOF', // CFA Franc BCEAO
  'BM': 'BMD', // Bermuda -> Bermudian Dollar
  'IN': 'INR', // India -> Indian Rupee
  'BT': 'BTN', // Bhutan -> Ngultrum
  'BO': 'BOB', // Bolivia -> Boliviano
  'BA': 'BAM', // Bosnia -> Convertible Mark
  'BW': 'BWP', // Botswana -> Pula
  'NO': 'NOK', // Norway -> Norwegian Krone
  'BR': 'BRL', // Brazil -> Brazilian Real
  'BN': 'BND', // Brunei -> Brunei Dollar
  'BG': 'BGN', // Bulgaria -> Bulgarian Lev
  'BI': 'BIF', // Burundi -> Burundi Franc
  'CV': 'CVE', // Cape Verde -> Cape Verde Escudo
  'KH': 'KHR', // Cambodia -> Riel
  'XA': 'XAF', // CFA Franc BEAC
  'CA': 'CAD', // Canada -> Canadian Dollar
  'KY': 'KYD', // Cayman Islands -> Cayman Islands Dollar
  'CL': 'CLP', // Chile -> Chilean Peso
  'CN': 'CNY', // China -> Yuan Renminbi
  'CO': 'COP', // Colombia -> Colombian Peso
  'KM': 'KMF', // Comoros -> Comoro Franc
  'CD': 'CDF', // Congo DR -> Congolese Franc
  'NZ': 'NZD', // New Zealand -> New Zealand Dollar
  'CR': 'CRC', // Costa Rica -> Costa Rican Colon
  'CU': 'CUP', // Cuba -> Cuban Peso
  'CZ': 'CZK', // Czech Republic -> Czech Koruna
  'DK': 'DKK', // Denmark -> Danish Krone
  'DJ': 'DJF', // Djibouti -> Djibouti Franc
  'DO': 'DOP', // Dominican Republic -> Dominican Peso
  'EG': 'EGP', // Egypt -> Egyptian Pound
  'SV': 'SVC', // El Salvador -> El Salvador Colon
  'ER': 'ERN', // Eritrea -> Nakfa
  'SZ': 'SZL', // Eswatini -> Lilangeni
  'ET': 'ETB', // Ethiopia -> Ethiopian Birr
  'FK': 'FKP', // Falkland Islands -> Falkland Islands Pound
  'FJ': 'FJD', // Fiji -> Fiji Dollar
  'XP': 'XPF', // CFP Franc
  'GM': 'GMD', // Gambia -> Dalasi
  'GE': 'GEL', // Georgia -> Lari
  'GH': 'GHS', // Ghana -> Ghana Cedi
  'GI': 'GIP', // Gibraltar -> Gibraltar Pound
  'GT': 'GTQ', // Guatemala -> Quetzal
  'GB': 'GBP', // UK -> Pound Sterling
  'GN': 'GNF', // Guinea -> Guinea Franc
  'GY': 'GYD', // Guyana -> Guyana Dollar
  'HT': 'HTG', // Haiti -> Gourde
  'HN': 'HNL', // Honduras -> Lempira
  'HK': 'HKD', // Hong Kong -> Hong Kong Dollar
  'HU': 'HUF', // Hungary -> Forint
  'IS': 'ISK', // Iceland -> Iceland Krona
  'ID': 'IDR', // Indonesia -> Rupiah
  'XD': 'XDR', // SDR
  'IR': 'IRR', // Iran -> Iranian Rial
  'IQ': 'IQD', // Iraq -> Iraqi Dinar
  'IL': 'ILS', // Israel -> New Israeli Sheqel
  'JM': 'JMD', // Jamaica -> Jamaican Dollar
  'JP': 'JPY', // Japan -> Yen
  'JO': 'JOD', // Jordan -> Jordanian Dinar
  'KZ': 'KZT', // Kazakhstan -> Tenge
  'KE': 'KES', // Kenya -> Kenyan Shilling
  'KP': 'KPW', // North Korea -> North Korean Won
  'KR': 'KRW', // South Korea -> Won
  'KW': 'KWD', // Kuwait -> Kuwaiti Dinar
  'KG': 'KGS', // Kyrgyzstan -> Som
  'LA': 'LAK', // Laos -> Kip
  'LB': 'LBP', // Lebanon -> Lebanese Pound
  'LS': 'LSL', // Lesotho -> Loti
  'ZA': 'ZAR', // South Africa -> Rand
  'LR': 'LRD', // Liberia -> Liberian Dollar
  'LY': 'LYD', // Libya -> Libyan Dinar
  'CH': 'CHF', // Switzerland -> Swiss Franc
  'MO': 'MOP', // Macao -> Pataca
  'MK': 'MKD', // North Macedonia -> Denar
  'MG': 'MGA', // Madagascar -> Malagasy Ariary
  'MW': 'MWK', // Malawi -> Malawi Kwacha
  'MY': 'MYR', // Malaysia -> Malaysian Ringgit
  'MV': 'MVR', // Maldives -> Rufiyaa
  // Add more as needed
};

const getCurrencyFromCountryCode = (code) => {
  return countryToCurrencyMap[code?.toUpperCase()] || 'USD';
};

export const currencyFormatter = (number, currency = 'USD') => {
  if (!number && number !== 0) return '';
  
  let validatedCurrency = currency;

  // Convert country code to currency code
  if (currency && countryToCurrencyMap[currency.toUpperCase()]) {
    validatedCurrency = countryToCurrencyMap[currency.toUpperCase()];
  }

  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: validatedCurrency || 'USD',
      maximumFractionDigits: 0,
    });
    return formatter.format(number);
  } catch (error) {
    console.warn(`Invalid currency code: ${currency}, falling back to USD`);
    // Fallback to USD
    const fallbackFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
    return fallbackFormatter.format(number);
  }
};

export const convertToSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '-');
};

export {
  rangeOfFullYears,
  getDaysInMonth,
  rangeOfFullYearsWithFuture,
  isToday,
  isTomorrow,
  isAfterTomorrow,
  isAfterPresentTimeAndToday,
  isBeforeToday,
  isAfterPresentTime,
  meetingsByDate,
  isImageURL,
};

export const calculateChecksum = async (file, chunkSize) => {
  return new Promise((res, rej) => {
    let md5;
    let slice =
        File.prototype.slice ||
        File.prototype.mozSlice ||
        File.prototype.webkitSlice,
      chunks = Math.ceil(file.size / chunkSize),
      current_chunk = 0,
      spark = new SparkMD5.ArrayBuffer();

    function onload(e) {
      try {
        spark.append(e.target.result); // append chunk
        current_chunk++;
        if (current_chunk < chunks) {
          read_next_chunk();
        } else {
          md5 = spark.end();
          res({ md5, totalChunks: chunks });
        }
      } catch (e) {
        rej(e);
      }
    }

    function read_next_chunk() {
      try {
        var reader = new FileReader();
        reader.onload = onload;
        var start = current_chunk * chunkSize,
          end = Math.min(start + chunkSize, file.size);
        reader.readAsArrayBuffer(slice.call(file, start, end));
      } catch (e) {
        rej(e);
      }
    }
    read_next_chunk();
  });
};

export const postHasInsights = (post) => {
  const { like_count, dislike_count, comment_count, reposts_by } = post ?? {};
  return (
    (like_count ?? 0) + (dislike_count ?? 0) > 0 ||
    comment_count > 0 ||
    reposts_by?.length > 0
  );
};

export const cleanHTML = (str, nodes) => {
  // Do stuff here...
  function stringToHTML() {
    let parser = new DOMParser();
    let doc = parser.parseFromString(str, 'text/html');
    return doc.body;
  }

  // Convert the string to HTML
  let html = stringToHTML();
  return html;
};

export const imageLinkToFileObject = async (url, props) => {
  return new Promise((res, rej) => {
    fetch(url)
      .then(async (response) => {
        const contentType = props?.type ?? response.headers.get('content-type');
        const blob = await response.blob();
        const file = new File([blob], props?.fileName ?? randomId(), {
          contentType,
        });
        res(file);
        // access file here
      })
      .catch((e) => {
        rej(e);
      });
  });
};

export const getFileExtension = (fileName) => {
  return fileName?.substring(fileName?.lastIndexOf('.') + 1);
};

export function humanFileSize(bytes, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(dp) + ' ' + units[u];
}

export const isDir = (file) => file?.type === 'D';
export const isRootDir = (file) => file?.type === 'RD' && !file?.team_info?.id;
export const isTeamDir = (file) => file?.type === 'TD';
export const isSharedUser = (file) => file?.email || file?.slug;
export const isFile = (file) => file?.type === 'F';
